import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@hqo/ui-atoms';
import { Check, Loader2 } from 'lucide-react';

import { AddOnPayment } from 'components/add-on-payment/add-on-payment';
import { AddOnQuestions } from '../components/add-on-questions';
import { AddOnsOrdered } from '../components/add-ons-ordered';
import { addOnReceipt } from 'components/ordered-add-ons/add-on-receipt';
import { useBookingAddOns } from '../use-booking-add-ons.hook';
import { QuantityViolationNotice } from 'pages/booking-add-ons/components/quantity-violation-notice';
import { AddOnsDialog } from './add-ons-dialog';
import { AddOnsDialogFooter } from './add-ons-dialog-footer';
import { useRemoveAllAddOns } from './use-remove-all-add-ons.hook';
import { AddOnsPicker } from './add-ons-picker';

/**
 * RES-1367 — the post-booking add-on step, as the my-bookings page presents it.
 *
 * ⛔ THE STEP MACHINE IS NOT REIMPLEMENTED HERE. Every decision this screen acts on — which step is
 * showing, whether a party is locked, what the estimate comes to, when a form must be answered, where
 * "back" goes — comes from `useBookingAddOns`, the same hook `BookingAddOnsPage` drives. What differs
 * between the two is the surface: a sheet inside `ReceiptBooking` there, a ui-atoms dialog here.
 *
 * ⭐ WHY TWO SURFACES AT ALL. The sheet is right where it already is — the post-checkout flow, where
 * the tenant is mid-payment and the surface is the page. On my bookings the booking is a COLUMN of a
 * three-pane layout, and the sheet opened as a fixed-width RCL modal laid across all three of them.
 * Agreed with Mason on RES-1367: fork the presentation, never the behaviour.
 *
 * ⚠️ `AddOnQuestions` and `AddOnsOrdered` are SHARED, not forked. One is a federated form renderer and
 * the other is the order itself; neither is chrome, and a second copy of either would be two places
 * for the vendor's questions, or the record of what was bought, to drift.
 */
export const BookingAddOnsDialog = (): JSX.Element | null => {
  const intl = useIntl();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const {
    booking,
    bookingStart,
    isLoading,
    selectedCount,
    partySections,
    selectedPartyKey,
    isPartyLocked,
    isKept,
    estimateTotal,
    estimateCurrency,
    hasUnpriceable,
    quantityOf,
    minQuantityOf,
    isAtMax,
    isFixedQuantity,
    isAwaitingRanges,
    onIncrease,
    onDecrease,
    onRemove,
    isSubmitting,
    isAwaitingPayment,
    hasSubmitFailed,
    rangeViolations,
    step,
    hasForm,
    isResolvingForm,
    formDefinition,
    formValues,
    isFormValid,
    unresolvedAddOns,
    hasInvalidAnswers,
    onFormChange,
    onFormValidate,
    onContinue,
    onBackToPicker,
    onBackToBooking,
    payment,
    onPaymentSettled,
    onPaymentCharged,
    orderedAddOns,
    onViewMyBookings,
  } = useBookingAddOns();

  const onToggle = useCallback((addOnId: string) => {
    setExpandedId(current => (current === addOnId ? null : addOnId));
  }, []);

  /*
   * `onBackToBooking` is passed as the close: routing through the flow's own exit rather than a
   * local dismissal is what tears the picker's claim on the store down with it.
   */
  const { canRemoveAll, onRemoveAll, isRemoving, removeFailureMessageId } = useRemoveAllAddOns(
    booking,
    onBackToBooking,
  );

  /*
   * RES-1336 — billing's payment sheet, which is a DETOUR from this flow rather than a screen in it.
   * It is its own overlay portaled to `document.body`, so it draws on top of this dialog rather than
   * inside it, and the step machine knows nothing about it.
   *
   * RES-1367: ⛔ CLOSED UNTIL ASKED FOR. This opened itself the instant a session existed, so a
   * tenant who had just ordered was handed a card form over a confirmation they had not finished
   * reading -- and the one control on screen was a dismissal of something they never summoned. The
   * order stands whether or not it is paid here and the vendor can still bill, so the charge is
   * offered (`onPayNow` on the confirmation) rather than imposed.
   */
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);

  const onPayNow = useCallback(() => setIsPaymentOpen(true), []);

  const canPayNow = Boolean(payment.checkoutSessionUuid && !payment.charge);

  const onPaymentOpenChange = useCallback(
    (open: boolean) => {
      setIsPaymentOpen(open);

      /*
       * ⛔ FORWARD ON DISMISS. The add-ons were written before the sheet opened; only the charge is
       * outstanding, so closing it lands on the confirmation rather than back on a screen that
       * implies the order can still be edited.
       */
      if (!open) {
        onPaymentSettled();
      }
    },
    [onPaymentSettled],
  );

  /*
   * Esc while billing's sheet is up must close the sheet, not this step: our document keydown capture
   * is registered first and `AddOnsDialog` refuses, so billing never sees the key. Routed through
   * `onPaymentOpenChange` so it still arms the settle window.
   */
  const onDismissPayment = useCallback(() => onPaymentOpenChange(false), [onPaymentOpenChange]);

  /*
   * The footer's exit and the confirmation's own exit are rendered here, so `AddOnsDialog`'s
   * `isDetourOpen` guard does not cover them: calling `onBackToBooking` unmounts the step and takes
   * the payment sheet's host with it.
   */
  const leaveUnlessPaying = useCallback(() => {
    if (isPaymentOpen) {
      return;
    }

    onBackToBooking();
  }, [isPaymentOpen, onBackToBooking]);

  /*
   * ⛔ `onPaymentComplete` DOES NOT CLOSE THE SHEET. The widget fires it on SUCCESS ONLY and then
   * renders its own success screen inside the still-open overlay; closing from here would tear that
   * away the instant it appeared. `onOpenChange(false)` remains the single exit, and the same one for
   * every ending — paid, declined, expired, or abandoned. The order stands in all of them and only
   * the charge differs.
   *
   * Rendered as a sibling of `AddOnsDialog`, never as its child: although the sheet portals to
   * `document.body`, as a child it was still mounted by the dialog, so closing the dialog unmounted
   * the charge in flight. `isDetourOpen` guards the same symptom; this ties the sheet's lifetime to
   * `payment.checkoutSessionUuid` rather than to whichever step is on screen.
   */
  /* The guarded path, as on the step: a charge an amend superseded must not read as settled. */
  const receipt = useMemo(() => addOnReceipt(payment.payment), [payment.payment]);

  const paymentSheet = payment.checkoutSessionUuid ? (
    <AddOnPayment
      checkoutSessionUuid={payment.checkoutSessionUuid}
      open={isPaymentOpen}
      onOpenChange={onPaymentOpenChange}
      onPaymentComplete={onPaymentCharged}
    />
  ) : null;

  const selectedParty = partySections.find(section => section.key === selectedPartyKey);
  const hasSelection = selectedCount > 0;

  /*
   * ⭐ Checked BEFORE the loading guard, and for the same reason the sheet checks it there: placing
   * the order re-reads the booking, so by the time this renders the booking is ineligible and
   * `isLoading` may be true again from that read. Guarding first would hide a screen the tenant has
   * already earned.
   */
  if (step === 'confirmation' && orderedAddOns) {
    return (
      <>
        <AddOnsDialog
          isDetourOpen={isPaymentOpen}
          onDetourDismiss={onDismissPayment}
          /*
           * VIS-2193. The ONE heading on this screen, with the confirmation's check beside it. The body
           * no longer repeats "Add-ons ordered" under it (`withHeading={false}` below), and focus lands
           * here when the order's submit button unmounts, so a screen reader announces the result.
           */
          title={intl.formatMessage({ id: 'add_on_ordered_title' })}
          titleIcon={<Check className="size-5 text-system-success-primary" />}
          focusTitle
          onClose={onBackToBooking}
          /* The body scrolls and this bar is pinned, so every payment control belongs here. */
          footer={
            <div className="flex w-full flex-col gap-2">
              {/*
              ⛔ THE ONLY PLACE THE WAIT CAN BE SHOWN. Between the write and the sheet are three
              asynchronous hops — the selection is written, a consumer creates the linked request,
              billing opens a session on it — and without a word for them the confirmation simply sits
              there and a payment sheet arrives unannounced a few seconds later.

              ⚠️ It says the charge is COMING, not that anything is unfinished: the add-ons ARE
              ordered by the time this renders, and the body above has already said so.
            */}
              {isAwaitingPayment && (
                <p
                  role="status"
                  aria-live="polite"
                  className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
                  data-testid="add-ons-dialog-awaiting-payment"
                >
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  {intl.formatMessage({ id: 'add_on_payment_preparing' })}
                </p>
              )}
              {/*
              Mutually exclusive with the spinner above: that is `!checkoutSessionUuid`, this is
              `checkoutSessionUuid && !charge`.
            */}
              {canPayNow && (
                <Button
                  type="button"
                  variant="default"
                  className="w-full"
                  onClick={onPayNow}
                  data-testid="add-ons-dialog-pay-now"
                >
                  {intl.formatMessage({ id: 'add_on_pay_now' })}
                </Button>
              )}
              {/*
              ⛔ ONE WAY OUT, where the sheet offers two. "View my bookings" is the sheet's second
              control because that flow ends on a receipt with the list somewhere else entirely —
              here the list is the page this dialog is sitting on top of, so the two controls would
              be the same destination twice.

              Steps down to `secondary` when the charge sits above it: two filled buttons stacked
              give no reading order.
            */}
              <Button
                type="button"
                variant={canPayNow ? 'secondary' : 'default'}
                className="w-full"
                onClick={leaveUnlessPaying}
                data-testid="add-ons-dialog-ordered-back"
              >
                {intl.formatMessage({ id: 'add_on_back_to_booking' })}
              </Button>
            </div>
          }
        >
          <AddOnsOrdered
            order={orderedAddOns}
            charge={receipt.charge}
            isAwaitingPrice={receipt.isAwaitingPrice}
            receiptTotal={receipt.total}
            onViewMyBookings={onViewMyBookings}
            onBackToBooking={leaveUnlessPaying}
            withActions={false}
            withHeading={false}
            /*
             * No `onPayNow` here: this dialog pins the charge in its footer, so the row `AddOnsOrdered`
             * would draw sits at the foot of a scrolling body. Surfaces with no pinned footer pass it.
             */
          />
        </AddOnsDialog>
        {paymentSheet}
      </>
    );
  }

  /*
   * ⚠️ The SURFACE stays open while the booking resolves. Returning a bare skeleton would unmount the
   * dialog and drop it back onto the pane for a frame, which reads as a surface that opened and shut.
   */
  if (isLoading) {
    return (
      <AddOnsDialog title={intl.formatMessage({ id: 'add_ons' })} onClose={onBackToBooking}>
        <div className="flex h-40 items-center justify-center" data-testid="add-ons-dialog-loading">
          <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      </AddOnsDialog>
    );
  }

  /* No resolved booking, no dialog. The bounce is driven from the hook. */
  if (!booking) {
    return null;
  }

  /*
   * ⭐ The questions are a separate SCREEN, switched locally. `hasForm` is re-checked here as well as
   * in the hook's own effect: the form can vanish underneath this render — the tenant clears their
   * last add-on, or the resolve fails open — and rendering the questions with a null definition would
   * crash a tenant who has already paid.
   */
  if (step === 'questions' && hasForm && formDefinition) {
    return (
      <>
        <AddOnsDialog
          isDetourOpen={isPaymentOpen}
          onDetourDismiss={onDismissPayment}
          /* RES-1357: an unpriced line makes this screen a quote request, as on the page surface. */
          title={intl.formatMessage({ id: hasUnpriceable ? 'add_on_quote_title' : 'checkout' })}
          onBack={onBackToPicker}
          onClose={onBackToBooking}
          footer={
            <AddOnsDialogFooter
              hasSelection={hasSelection}
              estimateTotal={estimateTotal}
              estimateCurrency={estimateCurrency}
              hasUnpriceable={hasUnpriceable}
              isSubmitting={isSubmitting}
              hasSubmitFailed={hasSubmitFailed}
              onContinue={onContinue}
              step={step}
              commits={step === 'questions' || !hasForm}
              /*
               * ⛔ `isFormValid` alone is not enough. The renderer normalises a lone `"a"` to `null`
               * before validating, so it reports valid while an answer contradicts its field's type.
               */
              isDisabled={!isFormValid || hasInvalidAnswers}
            />
          }
        >
          <AddOnQuestions
            definition={formDefinition}
            values={formValues}
            partyName={selectedParty?.name}
            partyLogoUrl={selectedParty?.logoUrl}
            onChange={onFormChange}
            onValidate={onFormValidate}
            hasUnpriceable={hasUnpriceable}
          />
          {/*
          Belongs on THIS screen, not the picker: the picker's CTA never submits while a form exists,
          so a notice there is unreachable while the tenant taps a dead button here. Blocks rather
          than warns — unlike `unresolvedAddOns`, this one the tenant can clear. Names no field:
          labels are rich text this app cannot flatten, and the renderer has marked each one inline.
        */}
          {hasInvalidAnswers && (
            <p
              role="alert"
              className="mt-4 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground"
              data-testid="add-ons-dialog-invalid-answers"
            >
              {intl.formatMessage({ id: 'add_on_answers_invalid' })}
            </p>
          )}
        </AddOnsDialog>
        {paymentSheet}
      </>
    );
  }

  return (
    <>
      <AddOnsDialog
        isDetourOpen={isPaymentOpen}
        onDetourDismiss={onDismissPayment}
        title={intl.formatMessage({ id: 'add_ons' })}
        onClose={onBackToBooking}
        footer={
          <AddOnsDialogFooter
            hasSelection={hasSelection}
            estimateTotal={estimateTotal}
            estimateCurrency={estimateCurrency}
            hasUnpriceable={hasUnpriceable}
            isSubmitting={isSubmitting}
            hasSubmitFailed={hasSubmitFailed}
            onContinue={onContinue}
            step={step}
            commits={step === 'questions' || !hasForm}
            /*
             * ⛔ Not the same as "no form". A resolve still running has not answered yet, and submitting
             * through it sends no answers to a vendor who did ask.
             */
            isDisabled={isResolvingForm || rangeViolations.length > 0}
            /* Picker branch only: beside "Continue" on the form step this reads as cancelling the form. */
            canRemoveAll={canRemoveAll}
            onRemoveAll={onRemoveAll}
            isRemoving={isRemoving}
            removeFailureMessageId={removeFailureMessageId}
          />
        }
      >
        <AddOnsPicker
          partySections={partySections}
          isPartyLocked={isPartyLocked}
          isKept={isKept}
          quantityOf={quantityOf}
          minQuantityOf={minQuantityOf}
          isAtMax={isAtMax}
          isFixedQuantity={isFixedQuantity}
          isAwaitingRanges={isAwaitingRanges}
          onIncrease={onIncrease}
          onDecrease={onDecrease}
          onRemove={onRemove}
          expandedId={expandedId}
          onToggle={onToggle}
          unresolvedAddOns={unresolvedAddOns}
          bookingStart={bookingStart}
        />
        <QuantityViolationNotice
          violations={rangeViolations}
          className="mt-4"
          testId="add-ons-dialog-quantity-notice"
        />
      </AddOnsDialog>
      {paymentSheet}
    </>
  );
};

export default BookingAddOnsDialog;
